import $http from '@vue-util/http';
import { fireTag } from '@vue-util/tagManager';
import { walletHasCreditCard } from '@/utils/hasCreditCard';
import { separateEntityNotificationMessage } from '@/utils/separateEntityMessage';
import { DEFAULT_DISCOUNT, MY_ACCOUNT_FALL_BACK_ERROR, CREDIT_CARD } from 'config';
import { fireErrorTag } from '@/utils/general';
import { useDashboardStore } from './useDashboardStore';

/* eslint-disable no-unused-vars*/
export const useWalletStore = defineStore('wallet', {
  state: () => ({
    extendToken: false,
    creditCards: null,
    creditForm: {
      notification: {
        type: '',
        message: null,
      },
    },
    digitalOffersResponse: {
      accounts: null,
      offers: null,
    },
    creditError: {
      notification: {
        type: '',
        message: '',
        title: '',
        headLine: '',
        url: '',
        urlTitle: '',
      },
    },
    walletError: {
      notification: {
        type: '',
        message: '',
      },
    },
    scheduledPayments: null,
    creditCardsKillSwitches: null,
    discountPercentage: null,
  }),
  getters: {
    getCreditCards: (state) => (state.creditCards ? state.creditCards.filter((card) => card.creditSummary[0].citiCardEnabled) : []),
    getWalletCards(state) {
      const arr = [];
      if (state.creditCards) {
        state.creditCards.forEach((card) => {
          card.creditSummary.forEach((item) => {
            if (item.walletCardEnabled) {
              arr.push({
                creditSummary: [item],
                dualLineCard: card.dualLineCard,
              });
            }
          });
        });
      }
      return arr;
    },
    getScheduledPayments: (state) => (cardId) => {
      if (state.scheduledPayments) {
        const autoPay = state.scheduledPayments.filter((payment) => payment.cardNumber === cardId);
        if (autoPay.length && autoPay[0].paymentEligibilityFlag.toUpperCase() === 'Y') {
          return autoPay;
        }
      }
      return null;
    },
    getScheduledPaymentsSettings: (state) => (cardId) => {
      if (state.scheduledPayments) {
        const scheduledPayment = state.scheduledPayments.filter((payment) => payment.cardNumber === cardId)[0];

        if (scheduledPayment?.scheduledPaymentInd) {
          const scheduledPaymentIndicator = scheduledPayment.scheduledPaymentInd.toLowerCase();
          const isAutopay = scheduledPaymentIndicator.charAt(1) === 'a';
          const isProcessing = scheduledPaymentIndicator.charAt(0) === 'p';
          const isSingle = scheduledPaymentIndicator.charAt(0) === 's';
          const hasNoScheduledPayment = scheduledPaymentIndicator.charAt(0) === 'n';

          // Scheduled Payment is NOT editable if payment is currently being processed
          scheduledPayment.editable = !isProcessing;

          // Display Autopay icon if the scheduledPayment is an autopay
          scheduledPayment.isAutopay = isAutopay;

          // Assigns citi-redirect action
          scheduledPayment.editAction = 'payments';

          if (isSingle && isAutopay) {
            scheduledPayment.editAction = 'autopay';
          }

          if (!hasNoScheduledPayment) {
            return scheduledPayment;
          }
        }
      }
      return null;
    },
    getPaymentEligibility() {
      return (cardId) => {
        const creditOnetimePaymentsEnabled = this.creditCardsKillSwitches?.creditOnetimePaymentsEnabled;
        const creditSchedulePaymentEnabled = this.creditCardsKillSwitches?.creditSchedulePaymentEnabled;
        const getScheduledPayments = this.getScheduledPayments(cardId);
        // reason code C should direct users to OTP regardless of paymentEligibilityFlag
        const reasonCodeC = this.getReasonCode(cardId) === 'C';
        const isOTPEligible = getScheduledPayments && getScheduledPayments[0].paymentEligibilityFlag.toUpperCase() === 'Y';
        const goToOneTimePayment = isOTPEligible || reasonCodeC;

        return creditSchedulePaymentEnabled && creditOnetimePaymentsEnabled && goToOneTimePayment;
      };
    },
    getReasonCode: (state) => (cardId) => {
      let eligibilityReasonCode = '';
      if (state.scheduledPayments) {
        const paymentCard = state.scheduledPayments.filter((payment) => payment.cardNumber === cardId);
        eligibilityReasonCode = paymentCard.length && paymentCard[0].eligibilityReasonCode ? paymentCard[0].eligibilityReasonCode.toUpperCase() : '';
      }
      return eligibilityReasonCode;
    },
  },
  actions: {
    setExtendToken(data) {
      this.extendToken = data;
    },
    setCreditCards(payload) {
      this.creditCards = payload;
    },
    deleteCardMutation(cardId) {
      this.creditCards = this.creditCards.filter((card) => card.creditSummary[0].cardId !== cardId);
    },
    setFormErrorNotification(error) {
      // incase of generic error, convert error.message to array
      if (!Array.isArray(error.message)) {
        const genericError = [];
        genericError.push(error.message);
        error.message = genericError;
      }
      this.creditForm.notification = error;
    },
    resetNotificationMessage() {
      this.creditForm.notification = { type: '', message: '' };
    },
    setScheduledPayments(payload) {
      this.scheduledPayments = payload.schedulePaymentSummary.schedulePayments;
    },
    setAmexOffers(payload) {
      this.digitalOffersResponse.accounts = payload.digitalOffersResponse.accounts;
      this.digitalOffersResponse.offers = payload.digitalOffersResponse.offers;
    },
    setCreditErrorNotification(error) {
      this.creditError.notification = error;
    },
    setWalletErrorNotification(error) {
      this.walletError.notification = error;
    },
    setCreditCardsKs(payload) {
      this.creditCardsKillSwitches = payload;
    },
    setDiscountDetails(payload) {
      this.discountPercentage = payload.DiscountResponse?.discount?.discountPercentage || DEFAULT_DISCOUNT;
    },
    fetchCreditCards() {
      $http
        .post(`/account-xapi/api/myaccount/credit/summary?cardType=CreditCards,Wallet&timeStamp=${Date.now()}`)
        .then((response) => {
          if (response.status === 200) {
            const cardObj = response.data?.cards;
            const ccObj = response.data?.cards?.creditCards;
            const ccErrorObj = response.data?.cards?.creditCards?.error;
            const walletErrorObj = response.data?.cards?.wallet?.error;
            const regCards = response.data?.cards?.creditCards?.registeredCards;
            const creditCardsKillSwitches = response.data?.cards?.creditCards?.killswitches;
            // check for any lostOrStolen cards
            const lostOrStolen = regCards?.some((card) => card.creditSummary.some((item) => item.lostOrStolen)) ?? false;
            const primaryCard = regCards?.filter((card) => card.creditSummary.some((item) => item.primary)) ?? [];

            // blank user Id technical exception... no credit cards cards obj returned
            if (cardObj) {
              this.setExtendToken(cardObj.extendToken);
            }
            if (cardObj?.error) {
              // no citi .. no wallet ... end call
              fireErrorTag(cardObj.error);
              this.setCreditErrorNotification({ type: 'error', message: cardObj.error.message || MY_ACCOUNT_FALL_BACK_ERROR });
              this.setWalletErrorNotification({ type: 'error', message: cardObj.error.message || MY_ACCOUNT_FALL_BACK_ERROR });
              this.setCreditCards([]);
              return;
            }
            // lostOrStolen is extra check when card is reported lostOrStolen but no notification Object in ccObj.notification
            if (lostOrStolen) {
              this.setCreditErrorNotification({
                type: 'info',
                title: 'REPLACEMENT CARD',
                message: 'Your replacement card will be automatically registered for online management when you go to the ',
                url: '/creditservice/creditacct/summary',
                urlTitle: 'CREDIT SUMMARY PAGE',
              });
            }
            // Classic consolidation notification check
            if (cardObj?.classicSunsetNotificationEnabled) {
              this.setWalletErrorNotification({
                type: 'info',
                title: 'Classic Consolidation',
                message:
                  'We recently notified you that your Macy’s American Express® Account is being closed. Your store account remains open. We’ve automatically updated the credit card number in your Wallet to reflect the credit card number for your store account. If you have any questions, please call 1-866-593-2543 for assistance.',
              });
            }
            if (ccObj.notifications) {
              if (ccObj.notifications[0].type === 'AS2') {
                fireErrorTag(ccObj.notifications[0]);
                this.setCreditErrorNotification({ type: 'error', message: CREDIT_CARD.errorMessage, title: ccObj.notifications[0].title });
              } else {
                this.setCreditErrorNotification({
                  type: 'info',
                  title: ccObj.notifications[0].title,
                  message: ccObj.notifications[0].message,
                  headLine: ccObj.notifications[0].headLine,
                  url: ccObj.notifications[0].url,
                  urlTitle: ccObj.notifications[0].urlTitle,
                });
              }
            }

            // Separate Entity Notification check
            if (primaryCard.length) {
              if (primaryCard[0].creditSummary[0].notifyInd) {
                const message = separateEntityNotificationMessage(primaryCard[0].creditSummary[0]);
                if (message) {
                  const dashboardStore = useDashboardStore();
                  dashboardStore.setGlobalNotification({
                    type: 'error',
                    closeButton: false,
                    message,
                  });
                  const fireTagData = {
                    event_name: 'separate entity message',
                    error_category: 'my account',
                    error_code: [''],
                    error_label: [''],
                    error_message: [message.replaceAll(/<[^>]*>/g, '')],
                    error_misc: ['my account'],
                    error_scope: ['global'],
                    separate_entity: true,
                  };
                  fireTag('link', fireTagData);
                }
              }
            }

            if (ccErrorObj) {
              // credit card error
              fireErrorTag(ccErrorObj);
              this.setCreditErrorNotification({ type: 'error', message: ccErrorObj.message || ccErrorObj.title || MY_ACCOUNT_FALL_BACK_ERROR }); // add fallback also
            }

            if (walletErrorObj) {
              // wallet down
              fireErrorTag(walletErrorObj);
              this.setWalletErrorNotification({ type: 'error', message: walletErrorObj.message || MY_ACCOUNT_FALL_BACK_ERROR });
            }

            if (ccErrorObj && walletErrorObj) {
              this.setCreditCards([]);
              return;
            }

            // happy path when no error
            if (regCards?.length > 0) {
              this.setCreditCards(regCards);
              this.setCreditCardsKs(creditCardsKillSwitches);
              this.fetchScheduledPayments();

              let creditCardApply = JSON.parse(sessionStorage.getItem('creditCardApply')) || {};
              const userHasCard = walletHasCreditCard(regCards);
              creditCardApply = { ...creditCardApply, ...{ userHasCard } };
              sessionStorage.setItem('creditCardApply', JSON.stringify(creditCardApply));
            } else {
              this.setCreditCards([]);
            }
          } else {
            // any other status code gives errors object
            fireErrorTag(response.data);
            const statusErrMsg = response.data?.errors?.error[0]?.message;
            this.setCreditErrorNotification({ type: 'error', message: statusErrMsg || MY_ACCOUNT_FALL_BACK_ERROR });
            this.setWalletErrorNotification({ type: 'error', message: statusErrMsg || MY_ACCOUNT_FALL_BACK_ERROR });
            this.setCreditCards([]);
          }
        })
        .catch((error) => {
          fireErrorTag(error);
          if (error.response?.status === 401) {
            window.location.href = '/account/signin';
          }
          const statusErrMsg = error.response?.data?.errors?.error[0]?.message;
          this.setCreditCards([]);
          this.setCreditErrorNotification({ type: 'error', message: statusErrMsg || MY_ACCOUNT_FALL_BACK_ERROR });
          this.setWalletErrorNotification({ type: 'error', message: statusErrMsg || MY_ACCOUNT_FALL_BACK_ERROR });
        });
    },

    updateCard(payload) {
      return new Promise((resolve, reject) => {
        $http
          .put('/account-xapi/api/wallet/updateTender', payload)
          .then((response) => {
            if (response.status === 200 && response.data.walletTender.msg) {
              this.fetchCreditCards();
              resolve(response);
            } else {
              const validationError = response.data?.walletTender;
              const errorMessages = validationError.errors ? validationError.errors.error : validationError.error || MY_ACCOUNT_FALL_BACK_ERROR;
              this.setFormErrorNotification({ type: 'error', message: errorMessages });
              reject(response);
            }
          })
          .catch((error) => {
            this.setFormErrorNotification({ type: 'error', message: error.message });
          });
      });
    },

    setAsDefaultCard(newDefaultUserCardGuid, previousDefaultUserCardGuid) {
      return new Promise((resolve, reject) => {
        $http
          .put(`/account-xapi/api/wallet/cards/default`, {
            defaultCardRequest: {
              newDefaultTenderGuid: newDefaultUserCardGuid,
              oldDefaultTenderGuid: previousDefaultUserCardGuid,
            },
          })
          .then((response) => {
            if (response.status === 200 && response.data.defaultCardResponse.updated) {
              this.fetchCreditCards();
              resolve(response);
            } else {
              const validationError = response.data?.defaultCardResponse;
              const errorMessages = validationError.updated ? MY_ACCOUNT_FALL_BACK_ERROR : validationError.message;
              this.setFormErrorNotification({ type: 'error', message: errorMessages });
              reject(response);
            }
          })
          .catch((error) => {
            this.setFormErrorNotification({ type: 'error', message: error.message });
            reject(error);
          });
      });
    },

    deleteCard(payload) {
      return new Promise((resolve, reject) => {
        $http
          .delete('/account-xapi/api/wallet/deleteTender', { data: payload })
          .then((response) => {
            if (response.status === 200 && response.data.walletTender.msg) {
              this.fetchCreditCards();
              resolve(response);
            } else {
              const errorMessage = response.data?.walletTender?.error?.message;
              this.setFormErrorNotification({ type: 'error', message: errorMessage || MY_ACCOUNT_FALL_BACK_ERROR });
              reject(response);
            }
          })
          .catch((error) => {
            this.setFormErrorNotification({ type: 'error', message: error.message });
          });
      });
    },

    postCreditCard({ formData, userId }) {
      return new Promise((resolve, reject) => {
        const url = `/account-xapi/api/wallet/${userId}/addtenders`;

        $http
          .post(url, formData)
          .then((response) => {
            if (response.status === 200 && response.data.walletTender.msg) {
              // now make a fetch creditSummary call test local
              this.fetchCreditCards();
              resolve(response);
            } else {
              const validationError = response.data?.walletTender;
              const errorMessages = validationError.errors ? validationError.errors.error : validationError.error || MY_ACCOUNT_FALL_BACK_ERROR;
              this.setFormErrorNotification({ type: 'error', message: errorMessages });
              reject(response);
            }
          })
          .catch((error) => {
            this.setFormErrorNotification({ type: 'error', message: error.message });
          });
      });
    },

    fetchScheduledPayments() {
      $http
        .get(`/account-xapi/api/creditservice/schedulepayments?timeStamp=${Date.now()}`)
        .then((response) => {
          if (response.status === 200 && response.data.schedulePaymentSummary) {
            this.setScheduledPayments(response.data);
          } else {
            const errorMessage = response.data?.error?.message;
            console.error('Error fetching ScheduledPayment data. ', errorMessage);
          }
        })
        .catch(() => {
          console.error('Error fetching ScheduledPayment data from /account-xapi/api/creditservice/schedulepayments ');
        });
    },

    fetchAmexOffers({ reqBody, reqHeaders, deviceType }) {
      $http
        .post(`/account-xapi/api/digitalofferservice?offerType=AMEX&_deviceType=${deviceType}&timeStamp=${Date.now()}`, reqBody, {
          headers: reqHeaders,
        })
        .then((response) => {
          this.setAmexOffers(response.data);
        })
        .catch(() => {});
    },

    async fetchDiscountDetails({ deviceType }) {
      try {
        const response = await $http.get(`/account-xapi/api/creditaquisitionservice/discount?_deviceType=${deviceType}`);
        this.setDiscountDetails(response.data);
      } catch (error) {
        this.setDiscountDetails('');
      }
    },
  },
});
